import * as THREE from 'three';
import { Weapon, WeaponConfig } from './Weapon';
import { EventBus } from '../core/EventBus';

const PLASMA_CONFIG: WeaponConfig = {
  name: 'Plasma Cannon',
  damage: 40,
  fireRate: 1.5, // 1.5 shots per second
  projectileSpeed: 80,
  ammoMax: 20,
  reloadTime: 2.5,
  spread: 0.03,
  automatic: true,
  projectileColor: new THREE.Color(0x8000ff),
  projectileSize: 0.4,
};

interface PlasmaProjectile {
  mesh: THREE.Mesh;
  glow: THREE.Mesh;
  direction: THREE.Vector3;
  speed: number;
  damage: number;
  spawnTime: number;
  lifetime: number;
}

// Callback for AOE damage
export type AOEDamageCallback = (
  center: THREE.Vector3,
  radius: number,
  damage: number
) => void;

export class PlasmaCannon extends Weapon {
  private scene: THREE.Scene;
  private projectiles: PlasmaProjectile[] = [];
  private targets: THREE.Object3D[] = [];
  private aoeDamageCallback: AOEDamageCallback | null = null;

  private readonly AOE_RADIUS = 5;
  private readonly PROJECTILE_LIFETIME = 3;

  constructor(scene: THREE.Scene) {
    super(PLASMA_CONFIG);
    this.scene = scene;
  }

  public setTargets(targets: THREE.Object3D[]): void {
    this.targets = targets;
  }

  public setAOEDamageCallback(callback: AOEDamageCallback): void {
    this.aoeDamageCallback = callback;
  }

  protected createProjectile(origin: THREE.Vector3, direction: THREE.Vector3): void {
    // Create plasma ball mesh
    const geometry = new THREE.SphereGeometry(this.config.projectileSize, 16, 16);
    const material = new THREE.MeshBasicMaterial({
      color: 0x8000ff,
      transparent: true,
      opacity: 0.9,
    });
    const mesh = new THREE.Mesh(geometry, material);
    mesh.position.copy(origin);

    // Create outer glow
    const glowGeometry = new THREE.SphereGeometry(this.config.projectileSize * 2, 16, 16);
    const glowMaterial = new THREE.MeshBasicMaterial({
      color: 0xff00ff,
      transparent: true,
      opacity: 0.3,
    });
    const glow = new THREE.Mesh(glowGeometry, glowMaterial);
    mesh.add(glow);

    this.scene.add(mesh);

    this.projectiles.push({
      mesh,
      glow,
      direction: direction.clone().normalize(),
      speed: this.getEffectiveProjectileSpeed(),
      damage: this.getEffectiveDamage(),
      spawnTime: performance.now() / 1000,
      lifetime: this.PROJECTILE_LIFETIME,
    });
  }

  public update(): void {
    super.update();

    const currentTime = performance.now() / 1000;

    for (let i = this.projectiles.length - 1; i >= 0; i--) {
      const proj = this.projectiles[i];

      // Check lifetime
      if (currentTime - proj.spawnTime > proj.lifetime) {
        this.destroyProjectile(i);
        continue;
      }

      // Move projectile
      const dt = 1 / 60; // Approximate delta
      proj.mesh.position.add(proj.direction.clone().multiplyScalar(proj.speed * dt));

      // Pulse effect
      const pulse = 1 + Math.sin((currentTime - proj.spawnTime) * 10) * 0.2;
      proj.glow.scale.setScalar(pulse);

      // Check ground collision
      if (proj.mesh.position.y < 0) {
        this.explode(proj, i);
        continue;
      }

      // Check target collisions
      for (const target of this.targets) {
        if (!target.userData.health) continue;

        const dist = proj.mesh.position.distanceTo(target.position);
        const hitRadius = (target.userData.hitRadius as number) || 1;

        if (dist < hitRadius + this.config.projectileSize) {
          this.explode(proj, i);
          break;
        }
      }
    }
  }

  private explode(proj: PlasmaProjectile, index: number): void {
    const center = proj.mesh.position.clone();

    // Create explosion visual
    this.createExplosionEffect(center);

    // Apply AOE damage
    if (this.aoeDamageCallback) {
      this.aoeDamageCallback(center, this.AOE_RADIUS, proj.damage);
    } else {
      // Fallback: damage nearby targets directly
      for (const target of this.targets) {
        if (!target.userData.health) continue;

        const dist = center.distanceTo(target.position);
        if (dist <= this.AOE_RADIUS) {
          // Damage falls off with distance
          const falloff = 1 - dist / this.AOE_RADIUS;
          const damage = proj.damage * falloff;

          const entity = target.userData.entity;
          if (entity && typeof entity.takeDamage === 'function') {
            entity.takeDamage(damage);
          } else {
            target.userData.health = Math.max(0, target.userData.health - damage);
            EventBus.emit('target:damaged', {
              id: target.userData.id,
              damage,
              healthRemaining: target.userData.health,
            });
          }
        }
      }
    }

    // Emit hit event
    EventBus.emit('projectile:hit', {
      position: { x: center.x, y: center.y, z: center.z },
      targetType: 'explosion',
    });

    this.destroyProjectile(index);
  }

  private createExplosionEffect(center: THREE.Vector3): void {
    // Expanding ring
    const ringGeometry = new THREE.RingGeometry(0.5, this.AOE_RADIUS, 32);
    const ringMaterial = new THREE.MeshBasicMaterial({
      color: 0xff00ff,
      transparent: true,
      opacity: 0.8,
      side: THREE.DoubleSide,
    });
    const ring = new THREE.Mesh(ringGeometry, ringMaterial);
    ring.position.copy(center);
    ring.position.y = 0.1;
    ring.rotation.x = -Math.PI / 2;
    this.scene.add(ring);

    // Sphere flash
    const sphereGeometry = new THREE.SphereGeometry(this.AOE_RADIUS * 0.5, 16, 16);
    const sphereMaterial = new THREE.MeshBasicMaterial({
      color: 0x8000ff,
      transparent: true,
      opacity: 0.6,
    });
    const sphere = new THREE.Mesh(sphereGeometry, sphereMaterial);
    sphere.position.copy(center);
    this.scene.add(sphere);

    // Animate and remove
    const startTime = performance.now();
    const duration = 300;

    const animate = () => {
      const elapsed = performance.now() - startTime;
      const t = elapsed / duration;

      if (t >= 1) {
        this.scene.remove(ring);
        this.scene.remove(sphere);
        ring.geometry.dispose();
        (ring.material as THREE.Material).dispose();
        sphere.geometry.dispose();
        (sphere.material as THREE.Material).dispose();
        return;
      }

      ring.scale.setScalar(1 + t * 2);
      ringMaterial.opacity = 0.8 * (1 - t);

      sphere.scale.setScalar(1 + t * 3);
      sphereMaterial.opacity = 0.6 * (1 - t);

      requestAnimationFrame(animate);
    };
    animate();
  }

  private destroyProjectile(index: number): void {
    const proj = this.projectiles[index];
    this.scene.remove(proj.mesh);
    proj.mesh.geometry.dispose();
    (proj.mesh.material as THREE.Material).dispose();
    proj.glow.geometry.dispose();
    (proj.glow.material as THREE.Material).dispose();
    this.projectiles.splice(index, 1);
  }

  public destroy(): void {
    for (let i = this.projectiles.length - 1; i >= 0; i--) {
      this.destroyProjectile(i);
    }
  }
}
